import { useState, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import { Helmet } from "react-helmet-async";
import { kml } from "@tmcw/togeojson";
import {
  Layers,
  ChevronDown,
  ChevronUp,
  ZoomIn,
  MapPin,
  Info,
} from "lucide-react";
import L from "leaflet";
import capasConfig from "@/data/capas.json";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

function ZoomToLayerButton({ bounds, layerName }) {
  const map = useMap();

  const handleZoom = () => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  };

  return (
    <button
      onClick={handleZoom}
      className="flex items-center gap-1 text-xs text-[#22c55e] hover:underline"
      title={`Zoom a ${layerName}`}
    >
      <ZoomIn size={14} />
      Zoom
    </button>
  );
}

function MapController({ layers, activeLayerIds }) {
  const map = useMap();

  useEffect(() => {
    // Calculate bounds of all active layers
    const activeLayers = layers.filter(
      (l) => activeLayerIds.includes(l.id) && l.bounds
    );
    if (activeLayers.length > 0) {
      const allBounds = activeLayers.reduce((acc, layer) => {
        return acc ? acc.extend(layer.bounds) : L.latLngBounds(layer.bounds);
      }, null);
      if (allBounds && allBounds.isValid()) {
        map.fitBounds(allBounds, { padding: [50, 50] });
      }
    }
  }, []);

  return null;
}

export default function Mapa() {
  const [layers, setLayers] = useState([]);
  const [activeLayers, setActiveLayers] = useState(
    capasConfig.filter((c) => c.activa).map((c) => c.id)
  );
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [error, setError] = useState(null);

  // Load KML files and convert to GeoJSON
  useEffect(() => {
    const loadKMLFiles = async () => {
      try {
        const loadedLayers = await Promise.all(
          capasConfig.map(async (config) => {
            try {
              const response = await fetch(config.archivo);
              const text = await response.text();
              const parser = new DOMParser();
              const kmlDoc = parser.parseFromString(text, "text/xml");
              const geojson = kml(kmlDoc);

              // Calculate bounds
              let bounds = null;
              if (geojson.features && geojson.features.length > 0) {
                const allCoords = [];
                geojson.features.forEach((feature) => {
                  if (feature.geometry) {
                    const extractCoords = (coords) => {
                      if (Array.isArray(coords[0])) {
                        coords.forEach(extractCoords);
                      } else if (coords.length >= 2) {
                        allCoords.push([coords[1], coords[0]]);
                      }
                    };
                    if (feature.geometry.coordinates) {
                      extractCoords(feature.geometry.coordinates);
                    }
                  }
                });
                if (allCoords.length > 0) {
                  bounds = L.latLngBounds(allCoords);
                }
              }

              return {
                ...config,
                geojson,
                bounds,
                featureCount: geojson.features?.length || 0,
              };
            } catch (err) {
              console.error(`Error loading ${config.nombre}:`, err);
              return { ...config, geojson: null, error: true };
            }
          })
        );

        setLayers(loadedLayers);
        setLoading(false);
      } catch (err) {
        console.error("Error loading KML files:", err);
        setError("Error cargando las capas del mapa");
        setLoading(false);
      }
    };

    loadKMLFiles();
  }, []);

  const toggleLayer = useCallback((layerId) => {
    setActiveLayers((prev) =>
      prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
    );
  }, []);

  const getFeatureStyle = useCallback(
    (layerId) => ({
      color: capasConfig.find((c) => c.id === layerId)?.color || "#22c55e",
      weight: 2,
      opacity: 0.8,
      fillOpacity: 0.3,
    }),
    []
  );

  const onEachFeature = useCallback((feature, layer) => {
    const props = feature.properties || {};
    const name =
      props.NOM ||
      props.name ||
      props.Nom_munici ||
      props.MUNICIPI ||
      "Sin nombre";
    const municipio =
      props.Nom_munici || props.MUNICIPI || props.Municipi || "";
    const refCatastral = props.REF_CATAST || props.ref_catast || "";
    const superficie = props.Shape_Area || props.SUPERFICIE || props.AREA_COMPU;
    const isla = props.Illa || props.ILLA || "";

    let popupContent = `<h4>${name}</h4>`;
    if (municipio) popupContent += `<p><strong>Municipio:</strong> ${municipio}</p>`;
    if (isla) popupContent += `<p><strong>Isla:</strong> ${isla}</p>`;
    if (refCatastral) popupContent += `<p><strong>Ref. Catastral:</strong> ${refCatastral}</p>`;
    if (superficie) {
      const superficieM2 = parseFloat(superficie);
      popupContent += `<p><strong>Superficie:</strong> ${superficieM2.toLocaleString("es-ES", { maximumFractionDigits: 0 })} m²</p>`;
    }

    layer.bindPopup(popupContent);
  }, []);

  const activeLayerData = useMemo(
    () => layers.filter((l) => activeLayers.includes(l.id) && l.geojson),
    [layers, activeLayers]
  );

  return (
    <div className="min-h-screen pt-16 md:pt-20" data-testid="mapa-page">
      <Helmet>
        <title>Mapa de Suelo Público Disponible | SOSTRE BALEAR</title>
        <meta name="description" content="Mapa interactivo con 593.000 m² de parcelas de suelo público disponibles para el programa de vivienda industrializada en las Illes Balears." />
        <link rel="canonical" href="https://www.sostrebalear.com/mapa" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Mapa de Suelo Público | SOSTRE BALEAR" />
        <meta property="og:description" content="593.000 m² de suelo público disponible para vivienda asequible en Baleares." />
        <meta property="og:url" content="https://www.sostrebalear.com/mapa" />
        <meta property="og:image" content="https://www.sostrebalear.com/assets/logos/sostre-balear.png" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="SOSTRE BALEAR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mapa de Suelo Público | SOSTRE BALEAR" />
        <meta name="twitter:description" content="593.000 m² de suelo público disponible para vivienda asequible en Baleares." />
      </Helmet>
      <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] flex flex-col md:flex-row">
        {/* Sidebar / Panel */}
        <aside
          className={`bg-white border-b md:border-b-0 md:border-r border-slate-200 transition-all duration-300 ${
            panelOpen ? "h-auto md:w-80" : "h-auto md:w-14"
          }`}
        >
          {/* Panel Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <div
              className={`flex items-center gap-3 ${
                panelOpen ? "" : "md:hidden"
              }`}
            >
              <Layers className="text-[#22c55e]" size={20} />
              <h2 className="font-semibold text-slate-900">Capas</h2>
            </div>
            <button
              onClick={() => setPanelOpen(!panelOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label={panelOpen ? "Cerrar panel" : "Abrir panel"}
              data-testid="toggle-panel"
            >
              {panelOpen ? (
                <ChevronUp className="md:rotate-90" size={20} />
              ) : (
                <ChevronDown className="md:-rotate-90" size={20} />
              )}
            </button>
          </div>

          {/* Panel Content */}
          {panelOpen && (
            <div className="p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22c55e]" />
                </div>
              ) : error ? (
                <p className="text-red-500 text-sm">{error}</p>
              ) : (
                <>
                  {/* Layer Toggles */}
                  <div className="space-y-3">
                    {layers.map((layer) => (
                      <div
                        key={layer.id}
                        className="bg-slate-50 rounded-xl p-3"
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id={`layer-${layer.id}`}
                            checked={activeLayers.includes(layer.id)}
                            onChange={() => toggleLayer(layer.id)}
                            className="mt-1 w-4 h-4 rounded border-slate-300 text-[#22c55e] focus:ring-[#22c55e]"
                            data-testid={`toggle-${layer.id}`}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`layer-${layer.id}`}
                              className="text-sm font-medium text-slate-900 cursor-pointer flex items-center gap-2"
                            >
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: layer.color }}
                              />
                              {layer.nombre}
                            </label>
                            <p className="text-xs text-slate-500 mt-1">
                              {layer.featureCount} parcelas
                            </p>
{/* Zoom button handled in map */}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Legend */}
                  <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Info size={16} />
                      Leyenda
                    </h3>
                    <div className="space-y-2">
                      {capasConfig.map((config) => (
                        <div
                          key={config.id}
                          className="flex items-center gap-2 text-xs text-slate-600"
                        >
                          <span
                            className="w-4 h-4 rounded border"
                            style={{
                              backgroundColor: `${config.color}4D`,
                              borderColor: config.color,
                            }}
                          />
                          {config.nombre}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
                    <p>
                      <strong>Tip:</strong> Haz clic en una parcela para ver sus
                      atributos (municipio, referencia catastral, superficie).
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </aside>

        {/* Map Container */}
        <div className="flex-1 relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#22c55e] mx-auto mb-4" />
                <p className="text-slate-600">Cargando mapa...</p>
              </div>
            </div>
          ) : (
            <MapContainer
              center={[39.5, 2.7]}
              zoom={9}
              className="h-full w-full"
              style={{ background: "#f1f5f9" }}
              data-testid="leaflet-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <MapController layers={layers} activeLayerIds={activeLayers} />

              {activeLayerData.map((layer) => (
                <GeoJSON
                  key={layer.id}
                  data={layer.geojson}
                  style={() => getFeatureStyle(layer.id)}
                  onEachFeature={onEachFeature}
                />
              ))}
            </MapContainer>
          )}

          {/* Map Attribution */}
          <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-lg text-xs text-slate-600 z-[400]">
            <MapPin size={12} className="inline mr-1" />
            Illes Balears · Datos: SOSTRE BALEAR
          </div>
        </div>
      </div>
    </div>
  );
}
